import type {
  creditBalanceIntervalSchema,
  CreditBalanceType,
  FeatureType,
} from "schemas/agent-billing";

import * as React from "react";

import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import { toast } from "@replohq/lattice/components/toast/Toast";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { useRateCards } from "@v2/features/agent-billing/hooks/useRateCards";
import {
  agentBillingRouter,
  agentBillingUtils,
} from "@v2/trpc/routes/agentBilling";
import {
  adminCreditBalanceIntervalByType,
  creditBalanceTypeSchema,
} from "schemas/agent-billing";

const SELECT_CLASS = "rounded border border-border bg-white px-2 py-1 text-xs";
const intervalLabels = {
  month: "Monthly",
  one_off: "One time",
} satisfies Record<
  (typeof creditBalanceIntervalSchema.options)[number],
  string
>;

function StringSelect<OptionValueType extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
  getOptionLabel,
}: {
  label: string;
  value: OptionValueType;
  options: readonly OptionValueType[];
  onChange: (value: OptionValueType) => void;
  disabled?: boolean;
  getOptionLabel?: (value: OptionValueType) => string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-muted text-[10px]">{label}</span>
      <select
        value={value}
        // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        onChange={(event) => onChange(event.target.value as OptionValueType)}
        className={SELECT_CLASS}
        disabled={disabled}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {getOptionLabel?.(option) ?? option}
          </option>
        ))}
      </select>
    </label>
  );
}

export const AddCreditsForm: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const { rateCards } = useRateCards();

  const creditFeatures = React.useMemo(() => {
    const firstRateCard = rateCards?.[0];
    if (!firstRateCard) {
      return [];
    }
    return firstRateCard.features
      .filter((feature) => feature.featureType === "credits")
      .map((feature) => feature.featureType);
  }, [rateCards]);

  const [featureType, setFeatureType] = React.useState<FeatureType>("credits");
  const [balanceType, setBalanceType] =
    React.useState<CreditBalanceType>("purchase");
  const [amount, setAmount] = React.useState("");
  const interval = adminCreditBalanceIntervalByType[balanceType];

  const { mutateAsync, isPending, isError, error, reset } =
    agentBillingRouter.credits.grant.useMutation({
      onSuccess: (_data, variables) => {
        void agentBillingUtils.credits.find.invalidate();
        void agentBillingUtils.credits.findTransactions.invalidate();
        toast.success({
          title: `${variables.amount > 0 ? "Granted" : "Removed"} ${Math.abs(variables.amount)} ${variables.balanceType} credits`,
        });
        setAmount("");
      },
    });

  const handleSubmit = () => {
    if (!workspaceId || !amount || Number(amount) === 0) {
      return;
    }
    reset();
    void mutateAsync({
      workspaceId,
      featureType,
      amount: Number(amount),
      balanceType,
      transactionType: "admin",
    });
  };

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-lg border border-border bg-sheet p-3">
      <span className="text-muted text-[10px] font-medium uppercase tracking-wide">
        Add Credits (Admin)
      </span>
      <div className="flex items-end gap-2">
        <StringSelect
          label="Feature"
          value={featureType}
          options={creditFeatures}
          onChange={setFeatureType}
        />
        <StringSelect
          label="Balance Type"
          value={balanceType}
          options={creditBalanceTypeSchema.options}
          onChange={setBalanceType}
        />
        <StringSelect
          label="Interval"
          value={interval}
          options={[interval]}
          onChange={() => {}}
          disabled
          getOptionLabel={(value) => intervalLabels[value]}
        />
        <label className="flex flex-col gap-1">
          <span className="text-muted text-[10px]">Amount</span>
          <Input
            type="number"
            size="sm"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="100"
            className="w-24"
            UNSAFE_className="font-mono text-xs"
          />
        </label>
        <Button
          variant="primary"
          size="sm"
          disabled={!amount || Number(amount) === 0 || isPending}
          loading={isPending}
          onClick={handleSubmit}
        >
          Grant
        </Button>
      </div>
      {isError && <span className="text-xs text-rose">{error.message}</span>}
    </div>
  );
};
